import React, { useEffect, useState } from 'react';
import { IconButton, Link, Panel, Stack, Text, Toggle } from '@fluentui/react';
import IvicosStrings from 'kits/language/stringKit';
import { OrganizationDto } from 'services/client-api-wrapper/identity_provider/responses.dto';
import { INTEGRATION_LABEL_STYLE } from '../integrationLayout';
import { ColiboDeactivationModal } from './ColiboDeactivationModal';
import type { IColiboConfiguration } from './colibo.types';
import { ColiboSettingsForm, resolveColiboErrorMessage } from './ColiboSettingsForm';

const COLIBO_WEBSITE_URL = 'https://colibo.com';

interface IColiboSettingsProps {
    isColiboEnabled: boolean;
    coliboBaseUrl: string;
    coliboClientId: string;
    updateColiboConfig: (config: IColiboConfiguration) => Promise<OrganizationDto>;
    isOrgLoaded: boolean;
}

// Same flow as OpenProjectSettings: switching on opens a panel for the instance and OAuth client, and the
// org is only configured once Save succeeds. Users then connect their own Colibo account on the widget.
export const ColiboSettings: React.FC<IColiboSettingsProps> = ({ isColiboEnabled, coliboBaseUrl, coliboClientId, updateColiboConfig, isOrgLoaded }) => {
    const [isToggleOn, setIsToggleOn] = useState(isColiboEnabled);
    const [isSettingsPanelOpen, setIsSettingsPanelOpen] = useState(false);
    const [isDeactivationModalVisible, setIsDeactivationModalVisible] = useState(false);
    const [deactivationErrorMessage, setDeactivationErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        setIsToggleOn(isColiboEnabled);
    }, [isColiboEnabled]);

    const handleToggleChange = (_ev: React.MouseEvent<HTMLElement>, checked?: boolean): void => {
        setDeactivationErrorMessage(null);
        if (checked) {
            setIsToggleOn(true);
            setIsSettingsPanelOpen(true);
            return;
        }
        if (isColiboEnabled) {
            setIsDeactivationModalVisible(true);
        } else {
            setIsToggleOn(false);
        }
    };

    const handleConfirmDeactivate = async (): Promise<void> => {
        setIsDeactivationModalVisible(false);
        setDeactivationErrorMessage(null);
        try {
            await updateColiboConfig({});
        } catch (err) {
            console.error('Failed to disable Colibo integration:', err);
            setDeactivationErrorMessage(resolveColiboErrorMessage(err));
        }
    };

    return (
        <Stack tokens={{ childrenGap: 4 }}>
            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 12 }}>
                <Stack style={INTEGRATION_LABEL_STYLE}>
                    <Link href={COLIBO_WEBSITE_URL} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 600 }} data-test-id="colibo-website-link">
                        {IvicosStrings.coliboSettingsToggleLabel}
                    </Link>
                </Stack>
                <Stack.Item grow />
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                    <Toggle
                        checked={isToggleOn}
                        onText={IvicosStrings.providerEnabledLabel}
                        offText={IvicosStrings.providerDisabledLabel}
                        disabled={!isOrgLoaded}
                        onChange={handleToggleChange}
                        styles={{ root: { minWidth: 120, marginBottom: 0 } }}
                        data-test-id="enable-colibo-integration"
                    />
                    {isToggleOn && (
                        <>
                            <IconButton iconProps={{ iconName: 'Settings' }} onClick={() => setIsSettingsPanelOpen(true)} data-test-id="colibo-settings-icon" />
                            {isColiboEnabled ? (
                                <Text style={{ color: '#107C10', fontWeight: 600 }}>{IvicosStrings.coliboSettingsConfiguredStatus}</Text>
                            ) : (
                                <Text style={{ color: '#A80000', fontWeight: 600 }}>{IvicosStrings.coliboSettingsNotConfiguredStatus}</Text>
                            )}
                        </>
                    )}
                </Stack>
            </Stack>
            {deactivationErrorMessage && (
                <Text style={{ color: '#A80000' }} data-test-id="colibo-deactivation-error">
                    {deactivationErrorMessage}
                </Text>
            )}
            <Panel
                headerText={IvicosStrings.coliboSettingsToggleLabel}
                isOpen={isSettingsPanelOpen}
                onDismiss={() => setIsSettingsPanelOpen(false)}
                closeButtonAriaLabel={IvicosStrings.notificationCloseButton}
                data-test-id="colibo-settings-panel"
            >
                <ColiboSettingsForm
                    coliboBaseUrl={coliboBaseUrl}
                    coliboClientId={coliboClientId}
                    hasStoredSecret={isColiboEnabled}
                    isOpen={isSettingsPanelOpen}
                    updateColiboConfig={updateColiboConfig}
                    onSaveSuccess={() => setIsSettingsPanelOpen(false)}
                    onCancel={() => setIsSettingsPanelOpen(false)}
                />
            </Panel>
            <ColiboDeactivationModal
                isOpen={isDeactivationModalVisible}
                onCancel={() => setIsDeactivationModalVisible(false)}
                onConfirm={handleConfirmDeactivate}
            />
        </Stack>
    );
};
